import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { getAppApiURL } from '../utils/serverUrl';
import { logAutoLogoutDiagnostic } from '../utils/autoLogoutDiagnostics';


const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [expiresTime, setExpiresTime] = useState(null);
  const isRefreshingRef = useRef(false);
  const [redirectPathCode, setRedirectPathCode] = useState(null);
  const [authProcess, setAuthProcess] = useState(null);
  const checkMockup = process.env.REACT_APP_IS_MOCKUP;

  useEffect(() => {
    if (checkMockup === "true") {
      return;
    }
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setExpiresTime(decoded.exp);
      } catch (error) {
        console.error("Failed to decode token:", error);
      }
    } /* else {
      console.error("No token found in localStorage");
    } */
  }, [token]);

  const clearToken = (reason) => {
    logAutoLogoutDiagnostic("auth-token-cleared", { reason });
    setToken(null);
    setExpiresTime(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('usage_emulator_config');
  };

  useEffect(() => {
    // Keep this tab's auth state in sync when another tab logs out or refreshes the token.
    const handleStorage = (event) => {
      if (event.key === 'token' && !event.newValue) {
        logAutoLogoutDiagnostic("auth-token-cleared", {
          reason: "cleared-in-another-tab",
        });
        setToken(null);
        setExpiresTime(null);
      } else if (event.key === 'token' && event.newValue) {
        setToken(event.newValue);
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const refreshToken = async () => {
    if (isRefreshingRef.current) {
      logAutoLogoutDiagnostic("auth-refresh-skipped", { reason: "already-refreshing" });
      return;
    }

    isRefreshingRef.current = true;
    logAutoLogoutDiagnostic("auth-refresh-started");

    try {
      const response = await fetch(`${getAppApiURL()}/api/refresh_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          grant_type: 'refresh_token',
          refresh_token: localStorage.getItem('refreshToken'),
          client_id: process.env.REACT_APP_CLIENT_ID,
          /* client_secret: process.env.REACT_APP_CLIENT_SECRET */
        })
      });

      if (!response.ok) {
        logAutoLogoutDiagnostic("auth-refresh-failed", {
          reason: "http-error",
          status: response.status,
        });
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const newExpiresTime = jwtDecode(data.token).exp;
      logAutoLogoutDiagnostic("auth-refresh-succeeded", {
        expiresAt: new Date(newExpiresTime * 1000).toISOString(),
      });
      console.table({
        now: Math.floor(new Date().getTime() / 1000).toLocaleString(),
        newExpiresTimeDate: new Date(newExpiresTime * 1000).toLocaleString(),
        diff: newExpiresTime - Math.floor(new Date().getTime() / 1000)
      })
      setToken(data.token);
      setExpiresTime(newExpiresTime);
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refresh_token);
    } catch (error) {
      console.error("Failed to refresh token", error);
      logAutoLogoutDiagnostic("auth-refresh-failed", {
        reason: "request-or-response-error",
        message: error.message,
      });
      clearToken("refresh-failed");
    } finally {
      isRefreshingRef.current = false;
    }
  };

  useEffect(() => {
    const checkTokenExpiration = () => {
      if (!expiresTime || isRefreshingRef.current) {
        return;
      }

      const currentTime = Math.floor(new Date().getTime() / 1000);
      console.log("Current time:", new Date(currentTime * 1000).toLocaleString(), "Expires time:", new Date(expiresTime * 1000).toLocaleString());
      if (currentTime >= expiresTime) {
        clearToken("access-token-expired");
        return;
      }

      // A check can land exactly five minutes before expiry. Refresh at that
      // boundary, otherwise the following five-minute check is already late.
      if (currentTime >= expiresTime - 300) {
        refreshToken();
      }
    };

    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 300000); // Check every five minutes

    return () => clearInterval(interval);
  }, [expiresTime, token]);

  return (
    <AuthContext.Provider value={{ token, setToken, redirectPathCode, setRedirectPathCode, authProcess, setAuthProcess }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
